import React from "react";
import { ArrowUpRight, ChartNoAxesCombined, Wallet } from "lucide-react";
import { money } from "../../shared/UI";

const series = [
  ["cotizaciones", "Cotizaciones", "#0082cb"],
  ["pedidos", "Pedidos", "#13426b"],
  ["ventas", "Ventas netas", "#8fc52b"],
];

export function balanceParts(balance) {
  const total = Math.max(0, Number(balance.saldo) || 0);
  const overdue = Math.min(total, Math.max(0, Number(balance.vencido) || 0));
  return {
    total,
    overdue,
    current: total - overdue,
    percent: total ? (overdue / total) * 100 : 0,
  };
}

export default function DashboardCharts({ data, onReceivables }) {
  const currencies = [
    ...new Set(
      Object.values(data.indicadores)
        .flat()
        .map((v) => v.moneda),
    ),
  ];
  return (
    <div className="dashboard-charts">
      {currencies.map((currency) => {
        const rows = series
          .filter(([key]) => key in data.indicadores)
          .map(([key, label, color]) => ({
            key,
            label,
            color,
            value: Number(
              data.indicadores[key].find((v) => v.moneda === currency)
                ?.importe || 0,
            ),
          }));
        const max = Math.max(0, ...rows.map((v) => Math.abs(v.value)));
        return (
          <section
            className="surface commercial-chart"
            key={currency}
            aria-label={`Actividad comercial en ${currency}`}
          >
            <div className="section-heading">
              <div>
                <ChartNoAxesCombined size={20} />
                <h2>Actividad comercial</h2>
              </div>
              <span className="currency-tag">{currency}</span>
            </div>
            <p>Importes del período seleccionado</p>
            <div className="commercial-bars">
              {rows.map((row) => (
                <div className="commercial-bar" key={row.key}>
                  <div>
                    <span>
                      <i style={{ background: row.color }} />
                      {row.label}
                    </span>
                    <strong>{money(row.value, currency)}</strong>
                  </div>
                  <div className="bar-track" aria-hidden="true">
                    <div
                      style={{
                        width: `${max ? (Math.abs(row.value) / max) * 100 : 0}%`,
                        background: row.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <small className="chart-note">
              Cada indicador es independiente; los importes no se suman entre
              documentos.
            </small>
          </section>
        );
      })}
      {onReceivables &&
        data.cartera_actual.map((balance) => {
          const { total, overdue, current, percent } = balanceParts(balance);
          return (
            <section
              className="surface balance-chart"
              key={balance.moneda}
              aria-label={`Cartera actual en ${balance.moneda}`}
            >
              <div className="section-heading">
                <div>
                  <Wallet size={20} />
                  <h2>Cartera actual</h2>
                </div>
                <span className="currency-tag">{balance.moneda}</span>
              </div>
              <div className="balance-content">
                <div
                  className="balance-ring"
                  role="img"
                  aria-label={`${percent.toFixed(1)}% del saldo está vencido`}
                  style={{
                    background: total
                      ? `conic-gradient(#db7a28 0% ${percent}%, #0082cb ${percent}% 100%)`
                      : "#e6ebf1",
                  }}
                >
                  <div>
                    <span>Saldo por cobrar</span>
                    <strong>{money(total, balance.moneda)}</strong>
                    <small>{balance.moneda}</small>
                  </div>
                </div>
                <div className="balance-legend">
                  <div>
                    <span>
                      <i className="current-dot" />
                      Por vencer / sin vencimiento
                    </span>
                    <strong>{money(current, balance.moneda)}</strong>
                  </div>
                  <div>
                    <span>
                      <i className="overdue-dot" />
                      Vencido
                    </span>
                    <strong>{money(overdue, balance.moneda)}</strong>
                  </div>
                  <button className="btn btn-link" onClick={onReceivables}>
                    Ver cuentas por cobrar <ArrowUpRight size={16} />
                  </button>
                </div>
              </div>
              <small className="chart-note">
                Saldo actual, independiente del período seleccionado.
              </small>
            </section>
          );
        })}
      {onReceivables && !data.cartera_actual.length && (
        <section className="surface balance-chart">
          <div className="section-heading">
            <div>
              <Wallet size={20} />
              <h2>Cartera actual</h2>
            </div>
          </div>
          <div className="empty">No hay saldos pendientes por cobrar.</div>
        </section>
      )}
    </div>
  );
}
